import { ParamDefRowView, BonusParam, bonusParamSchema } from "@bonusmap/api-types";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { ParameterComponent } from "../parameters-input/parameter-component.js";
import { BonusParamItemValue } from "./bonus-param-item-value.js";
import { StepsComponent } from "../base-components/steps-component.js";
import { PARAM_TYPE_SYMBOLS_MAP } from "../../utils/lists.js";
import { toastError, toastInfo } from "../../toasts.js";
import { BonusParamItemGroupComponent } from "./bonus-parameters-group.js";
import { isNumber } from "@bonusmap/shared_ui/library";
type BonusParamItemComponentState = "edit" | "view";
type BonusParamItemEventName = 
'status-changed' | 
'loaded-item' | 
'param-item-change' | 
'param-changed' |
'value-need-reset';
export class BonusParamItemComponent extends BaseWebComponent {
    static webComponentName = "bonus-param-item";
    protected _templateId: string = "bonus-param-item"; // Template ID for rendering
    
    /**
     * Override per dispatch con tipizzazione specifica per BonusParamItemEventName
     */
    public dispatch(
        eventName: BonusParamItemEventName,
        detail: any,
        bubbles = true,
        cancelable = true
    ): CustomEvent {
        return super.dispatch(eventName, detail, bubbles, cancelable);
    }
    private _parameterInput!: ParameterComponent;
    private _bonusParamItemValue!: BonusParamItemValue;
    private _bonusParamItemLink!: HTMLAnchorElement;

    get parameterInput(): ParameterComponent {
        return this._parameterInput;
    }
    set parameterInput(value: ParameterComponent) {
        this._parameterInput = value;
    }
    private _status: BonusParamItemComponentState = "view"; // Default status
    get status(): BonusParamItemComponentState {
        return this._status;
    }
    set status(value: BonusParamItemComponentState) {
        const changed = (value !== this._status);
        this._status = value;
        this.setStatusClass();
        if (changed) {
            this.dispatch('status-changed', { status: this._status });
        }
    }
    constructor() {
        super();
        this.setStatusClass();
        this.globalListeners();
    }

    get value(): BonusParam | null {
        const value = this._bonusParamItemValue?.value;
        const parameter_id = this._parameterInput?.value;
        if (value === undefined ||
            value === null ||
            parameter_id === undefined ||
            parameter_id === null ||
            !isNumber(parameter_id)) {
            return null;
        }
        const result = {
            parameter_id: parseInt(parameter_id),
            value
        } as BonusParam;
        const valid = bonusParamSchema.safeParse(result).success ? result : null;
        if (!valid) {
            return null;
        }
        return valid;
    }
    private loadedCount: number = 0; // Contatore per gli eventi 'loaded' dei figli
    get loaded(): boolean {
        return this.loadedCount >= 2; // Deve essere caricato sia parameterInput che bonusParamItemValue
    }
    private globalListeners() {
        this.addEventListener('loaded',(event:Event)=> {
            // sia parameterInput che bonusParamItemValue emettono l'evento 'loaded' quando sono pronti
            event.stopPropagation(); // Evita che l'evento si propaghi ulteriormente
            const ev: CustomEvent = event as CustomEvent;
            if (ev.detail && ev.detail.origin) {
                // il nome del componente che ha emesso l'evento è statico in ev.detail.origin 
                // console.log(`bonus-param-item – ricevuto evento 'loaded' da: `,ev.detail.origin.constructor.webComponentName);
                this.loadedCount++;
                if(this.loadedCount>= 2) {
                    this.classList.add('loaded');
                    // emette l'evento 'loaded' per notificare che il componente è completamente caricato
                    this.dispatch('loaded-item', { origin: this },true,true);
                }
            }
        })
        // [role="remove-param-item"]
        this.on("click", '[role="remove-param-item"]', (event: Event) => {
            event.preventDefault();
            const target: HTMLElement = event.target as HTMLElement;
            const bonusParametersGroup = target.closest<BonusParamItemGroupComponent>('bonus-parameters-group');
            if (bonusParametersGroup) {
                if (bonusParametersGroup.removeItem(this)) {
                    toastInfo("Parametro rimosso");
                }
                else {
                    toastError("Impossibile rimuovere il parametro");
                }
            }
        });
        
        this.addEventListener('value-need-reset', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            this._bonusParamItemValue?.resetValue();
        });
        // Listener per il cambiamento di valore di un componente figlio
        this.addEventListener('value-changed',  (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            const origin = ev.detail.origin;
            // console.log(`BonusParamItemComponent: value-changed from ${origin.constructor.webComponentName}`, ev.detail.value);
            const valid = this.value!=null && this.value!=undefined;
            this.dispatch('param-item-change', { value: this.value , origin: this , valid }, true, true);
            this.classList.toggle('valid', valid);
            this.classList.toggle('invalid', !valid);
            this.refreshUi();
        });
    }

    private setStatusClass(): void {
        this.classList.remove("edit", "view");
        this.classList.add(this.status);
    }

    private _currentParam!: ParamDefRowView | null;
    public get currentParam(): ParamDefRowView | null {
        return this._currentParam;
    }
    public set currentParam(value: ParamDefRowView | null) {
        if (this._currentParam != undefined && value !== this._currentParam) {
            console.log("BonusParamItemComponent: currentParam changed", value, this._currentParam);
            this.dispatch('value-need-reset', { value });
        }
        this._currentParam = value;
    }
    private refreshUi(): void {
        const bonusParamItemOperator: HTMLElement = this.querySelector('bonus-param-item-operator') as HTMLElement;
        const bonusParamItemValue: HTMLElement = this.querySelector('bonus-param-item-value') as HTMLElement;
        bonusParamItemOperator.textContent = "";
        bonusParamItemOperator.removeAttribute('data-tooltip');
        if (this._parameterInput) {
            const current = this._parameterInput.current;
            const paramTypeSymbolsMap = PARAM_TYPE_SYMBOLS_MAP;
            const paramTypeMap = this._parameterInput.paramTypeMap;
            if (current) {
                bonusParamItemOperator.textContent = paramTypeSymbolsMap[current.type_name];
                bonusParamItemOperator.setAttribute('data-tooltip', paramTypeMap[current.type_name] || current.type_name);
                this.dispatch('param-changed', { value: current });
            }
        }
        this.currentParam = this._parameterInput?.current || null;

    }
    protected afterRender(): void {
        this._bonusParamItemLink = this.querySelector('a[role="bonus-param-item-edit-close"]') as HTMLAnchorElement;
        this._bonusParamItemLink.addEventListener('click', (event: MouseEvent) => {
            event.preventDefault();
            this.status = "view"; // Set status to view when link is clicked
            this._parameterInput?.putInViewMode(); // Ensure parameter input is in view mode
            this._bonusParamItemValue?.putInViewMode(); // Ensure bonus param item value
        });
        this.refreshUi();
    }

    private onChildrenChangeStep(step: string, target: StepsComponent): void {
        // Determina se il componente che ha cambiato step è andato in view o edit
        const isTargetGoingToView = (target === this._parameterInput && step === this._parameterInput.steps.view) ||
            (target === this._bonusParamItemValue && step === this._bonusParamItemValue.steps.view);

        const isTargetGoingToEdit = (target === this._parameterInput && (step === this._parameterInput.steps.edit || step === this._parameterInput.steps.manage)) ||
            (target === this._bonusParamItemValue && step === this._bonusParamItemValue.steps.edit);

        if (isTargetGoingToView) {
            // Il componente va in view → porta tutto in view
            this.status = "view";

            // Sincronizza l'altro componente in view
            if (target === this._parameterInput && this._bonusParamItemValue) {
                this._bonusParamItemValue.putInViewMode();
            } else if (target === this._bonusParamItemValue && this._parameterInput) {
                this._parameterInput.putInViewMode();
            }

            this.refreshUi();

        } else if (isTargetGoingToEdit) {
            // Il componente va in edit → porta tutto in edit
            this.status = "edit";

            // Sincronizza l'altro componente in edit
            if (target === this._parameterInput && this._bonusParamItemValue) {
                this._bonusParamItemValue.putInEditMode();
            } else if (target === this._bonusParamItemValue && this._parameterInput) {
                this._parameterInput.putInEditMode();
            }
        }
    }
   
    protected render(): void {
        super.render();
        const parametersInput = this.querySelector('parameters-input');
        const bonusParamItemValue = this.querySelector('bonus-param-item-value');
        this._bonusParamItemValue = bonusParamItemValue as BonusParamItemValue;
        this._parameterInput = parametersInput as ParameterComponent;
        this.refreshUi();

        // this._parameterInput?.addEventListener('value-changed', (event: Event) => {
        //     event.stopPropagation(); // Evita che l'evento si propaghi ulteriormente
        //     this.refreshUi();
        // });

        // Listener unificato per step-changed
        this._parameterInput?.addEventListener('step-changed', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            const step = ev.detail.step;
            this.onChildrenChangeStep(step, this._parameterInput);
        });
        this._parameterInput?.addEventListener('before-step-changed', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            if (ev.detail.step === this._parameterInput.steps.view) {
                event.preventDefault(); // Prevent default behavior if going to view
            }
        });
        this._bonusParamItemValue?.addEventListener('step-changed', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            const step = ev.detail.step;
            this.onChildrenChangeStep(step, this._bonusParamItemValue);
        });
       
        this._bonusParamItemValue?.addEventListener('before-step-changed', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            if (ev.detail.step === this._bonusParamItemValue.steps.view) {
                event.preventDefault(); // Prevent default behavior if going to view
            }
        });
        // this._bonusParamItemValue?.addEventListener('value-changed', (event: Event) => {
        //     event.stopPropagation(); // Evita che l'evento si propaghi ulteriormente
        //     this.refreshUi();
        // });
    }
}